import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 10) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentInfo(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        // Continue polling
        setAttempts((prev) => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-display font-bold mb-4">
              Athuga greiðslustöðu...
            </h1>
            <p className="text-muted-foreground">
              Vinsamlegast bíðið á meðan við staðfestum greiðsluna.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-display font-bold mb-4 text-green-700">
              Greiðsla tókst!
            </h1>
            <p className="text-muted-foreground mb-6">
              Takk fyrir bókunina. Þú færð staðfestingu á netfangið þitt.
            </p>
            {paymentInfo && (
              <div className="bg-green-50 rounded-lg p-4 mb-6">
                <p className="text-lg font-semibold text-green-800">
                  €{paymentInfo.amount?.toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                </p>
              </div>
            )}
            <Button
              onClick={() => navigate("/")}
              className="w-full bg-primary hover:bg-primary-600"
            >
              Fara á forsíðu
            </Button>
          </>
        )}

        {status === "expired" && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-display font-bold mb-4 text-red-700">
              Greiðsla rann út
            </h1>
            <p className="text-muted-foreground mb-6">
              Greiðsluferli rann út á tíma. Vinsamlegast reyndu aftur.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="w-full bg-primary hover:bg-primary-600"
            >
              Reyna aftur
            </Button>
          </>
        )}

        {status === "timeout" && (
          <>
            <XCircle className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
            <h1 className="text-2xl font-display font-bold mb-4">
              Gat ekki staðfest greiðslu
            </h1>
            <p className="text-muted-foreground mb-6">
              Við gátum ekki staðfest greiðsluna. Athugaðu tölvupóst eða hafðu samband.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="w-full bg-primary hover:bg-primary-600"
            >
              Fara á forsíðu
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
